// Pure TypeScript port of BaseDashboard/DProduct/Services/DProductTreeService.AutoResolve
// and DProductTreePreviewModal.BuildPreferenceMap. Lets the React tool walk the tree
// entirely client-side — no server round-trip per click.
//
// Keep this in sync with the C# original. The behavior is intentionally identical.

import type { EmbedNodeDto } from "../types/tree";

/** Builds a parent→children map and finds roots from a flat node list. */
export interface TreeIndex {
  byId: Map<string, EmbedNodeDto>;
  childrenOf: Map<string | null, EmbedNodeDto[]>;
  roots: EmbedNodeDto[];
}

export function indexTree(nodes: EmbedNodeDto[]): TreeIndex {
  const byId = new Map<string, EmbedNodeDto>();
  const childrenOf = new Map<string | null, EmbedNodeDto[]>();
  for (const n of nodes) {
    byId.set(n.id, n);
    const key = n.parentId ?? null;
    let bucket = childrenOf.get(key);
    if (!bucket) {
      bucket = [];
      childrenOf.set(key, bucket);
    }
    bucket.push(n);
  }
  for (const bucket of childrenOf.values()) {
    bucket.sort((a, b) => a.sortOrder - b.sortOrder);
  }
  const roots = childrenOf.get(null) ?? [];
  return { byId, childrenOf, roots };
}

export function childrenOfNode(idx: TreeIndex, nodeId: string): EmbedNodeDto[] {
  return idx.childrenOf.get(nodeId) ?? [];
}

export interface AutoResolveResult {
  /** Full path of picked option ids from root to leaf. */
  fullPath: string[];
  /** Every Decision walked, in order. Mirrors `DecisionsVisited` in C#. */
  decisionsVisited: EmbedNodeDto[];
  /** The resolved leaf product id, when a ProductChoice was reached. */
  resolvedProductId: string | null;
  error: string | null;
}

/**
 * Walks the tree replaying `prefix` (validating each pick lives under the right Decision),
 * then auto-fills the rest using preference carry-over (when present) or first-child.
 *
 * Mirrors DProductTreeService.AutoResolve in C#.
 */
export function autoResolve(
  idx: TreeIndex,
  prefix: string[],
  optionPreferenceBySharedDecisionId?: Map<string, string>
): AutoResolveResult {
  const root = idx.roots.find(n => n.kind === "Decision");
  if (!root) {
    return {
      fullPath: [],
      decisionsVisited: [],
      resolvedProductId: null,
      error: "Tree has no root decision.",
    };
  }

  const path: string[] = [];
  const decisionsVisited: EmbedNodeDto[] = [root];
  let currentDecision = root;

  // Step 1: replay prefix
  for (let i = 0; i < prefix.length; i++) {
    const pickedId = prefix[i];
    const children = childrenOfNode(idx, currentDecision.id);
    const picked = children.find(c => c.id === pickedId);
    if (!picked) break; // stale id — fall through to auto-fill
    path.push(picked.id);
    if (picked.kind === "ProductChoice") {
      return {
        fullPath: path,
        decisionsVisited,
        resolvedProductId: picked.refProductId,
        error: null,
      };
    }
    const next = childrenOfNode(idx, picked.id).find(c => c.kind === "Decision");
    if (!next) {
      return {
        fullPath: path,
        decisionsVisited,
        resolvedProductId: null,
        error: "Tree is incomplete: a Choice has no Decision under it.",
      };
    }
    currentDecision = next;
    decisionsVisited.push(currentDecision);
  }

  // Step 2: auto-fill using preferences then first-child
  let safety = 0;
  while (safety++ < 200) {
    const pick = chooseOption(idx, currentDecision, optionPreferenceBySharedDecisionId);
    if (!pick) {
      return {
        fullPath: path,
        decisionsVisited,
        resolvedProductId: null,
        error: "Tree is incomplete: a Decision has no options.",
      };
    }
    path.push(pick.id);
    if (pick.kind === "ProductChoice") {
      return {
        fullPath: path,
        decisionsVisited,
        resolvedProductId: pick.refProductId,
        error: null,
      };
    }
    const next = childrenOfNode(idx, pick.id).find(c => c.kind === "Decision");
    if (!next) {
      return {
        fullPath: path,
        decisionsVisited,
        resolvedProductId: null,
        error: "Tree is incomplete: a Choice has no Decision under it.",
      };
    }
    currentDecision = next;
    decisionsVisited.push(currentDecision);
  }

  return {
    fullPath: path,
    decisionsVisited,
    resolvedProductId: null,
    error: "Auto-resolve depth limit hit.",
  };
}

function chooseOption(
  idx: TreeIndex,
  decision: EmbedNodeDto,
  prefs?: Map<string, string>
): EmbedNodeDto | null {
  const children = childrenOfNode(idx, decision.id);
  if (prefs && decision.sharedLabelId) {
    const preferredOptionSharedId = prefs.get(decision.sharedLabelId);
    if (preferredOptionSharedId) {
      const preferred = children.find(c => c.sharedLabelId === preferredOptionSharedId);
      if (preferred) return preferred;
    }
  }
  return children[0] ?? null;
}

/**
 * Mirrors DProductTreePreviewModal.BuildPreferenceMap. For each Decision walked that has
 * a SharedLabelId, record the SharedLabelId of the option that was picked under it.
 * AutoResolve uses this to keep picks across linked decisions when the user changes
 * a path mid-tree (e.g. switching Tall→Short keeps Steel under the new Frame decision
 * when both Frame Decisions and both Steel options are linked).
 */
export function buildPreferenceMap(
  decisionsVisited: EmbedNodeDto[],
  fullPath: string[],
  idx: TreeIndex
): Map<string, string> {
  const prefs = new Map<string, string>();
  const limit = Math.min(decisionsVisited.length, fullPath.length);
  for (let i = 0; i < limit; i++) {
    const decision = decisionsVisited[i];
    if (!decision.sharedLabelId) continue;
    const pickedId = fullPath[i];
    const picked = idx.byId.get(pickedId);
    if (!picked?.sharedLabelId) continue;
    prefs.set(decision.sharedLabelId, picked.sharedLabelId);
  }
  return prefs;
}
